// YERİ: src/app/domains/website/layout/web-layout.ts
// melbarrel.com'un kabuğu.
//
// Üst menü ve alt bilgi HARDCODED DEĞİL: Ana Şablon'un (CMS > Sayfalar > Ana
// Şablon) bölümlerinden gelir. Yani menüyü değiştirmek için kod dağıtmak
// gerekmez. Çerçeve BURADA çizilir, sayfa içeriği <router-outlet>'te —
// böylece rota değişiminde menü yeniden kurulmaz (yanıp sönme olmaz) ve blog /
// yasal metin sayfaları da CMS sayfası olmadıkları hâlde aynı çerçeveyi alır.

import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { Component, computed, effect, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SiteSection } from '@/app/domains/site/core/tenant-web.service';
import { MktChromeStore } from '../core/chrome.store';
import { LanguageStore } from '../core/language.store';
import { splitMaster } from '../core/section-mapper';
import { SeoService } from '../core/seo.service';
import { WebCmsService } from '../core/web-cms.service';
import { WebSection } from '../core/web.models';
import { MktHost } from '../sections/mkt-host';

/**
 * Yapışkan menü yüksekliği ölçülemezse kullanılacak değer — çıpa hedefi menünün
 * altında kalmasın diye. Gerçek yükseklik çalışma anında ölçülür (aşağıya bak).
 */
const STICKY_HEADER_FALLBACK = 112;

/** Başlığın hemen dibine yapışmasın — çıpa hedefinin üstünde nefes payı. */
const ANCHOR_BREATHING_ROOM = 16;

@Component({
  selector: 'web-layout',
  standalone: true,
  imports: [RouterOutlet, MktHost],
  // 'mkt' sınıfı vitrinin marka token'larını (--mkt-accent…) açar. Panelin
  // indigo teması bu ağacın DIŞINDA kalır — iki kimlik birbirine karışmaz.
  host: { class: 'mkt flex min-h-dvh flex-col' },
  template: `
    @for (section of top(); track section.id) {
      <mkt-host [section]="section" />
    }

    <!-- Menü perdesi.
         MENÜNÜN İÇİNDE ÇİZİLEMEZ: menü kabuğunda backdrop-filter var (buzlu
         cam) ve backdrop-filter, position:fixed torunları için içeren blok
         oluşturur — perde viewport yerine menü şeridine göre konumlanır.
         Durum MktChromeStore'da paylaşılıyor, perde burada (menünün dışında).
         z-40: menü z-50'de, perde onun altında ama sayfa içeriğinin üstünde. -->
    @if (chrome.overlayOpen()) {
      <div class="fixed inset-0 z-40 bg-[color:var(--mkt-ink)]/25 backdrop-blur-[3px]"
        (click)="chrome.closeAll()" aria-hidden="true"></div>
    }

    <main class="flex-1">
      <router-outlet />
    </main>

    @for (section of bottom(); track section.id) {
      <mkt-host [section]="section" />
    }
  `,
})
export class WebLayout implements OnInit {
  private cms = inject(WebCmsService);
  private scroller = inject(ViewportScroller);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private langStore = inject(LanguageStore);
  private seo = inject(SeoService);
  private doc = inject(DOCUMENT);

  protected chrome = inject(MktChromeStore);

  private master = signal<{ top: SiteSection[]; bottom: SiteSection[] }>({ top: [], bottom: [] });

  protected top = computed(() => this.master().top);
  protected bottom = computed(() => this.master().bottom);

  /**
   * Site geneli yapısal veri — HER sayfada durur (sayfaya özgü olanlar, örn.
   * FAQPage, bunun ÜSTÜNE eklenir).
   *
   * İki hedefi var:
   *  • Google: bilgi paneli, marka kutusu, yazılım zengin sonucu.
   *  • Yapay zekâ asistanları: "restoranlar için hangi yazılımı önerirsin"
   *    sorusuna cevap verirken ürünün ne olduğunu, kime hitap ettiğini ve
   *    fiyatını HTML'i yorumlamadan okuyabilsinler.
   */
  ngOnInit(): void {
    this.seo.setJsonLd('org', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': '#organization',
          name: 'Melbarrel',
          url: '/',
          description:
            'Business management software for service businesses: CRM, bookings, staff, '
            + 'invoicing and a website builder in one system.',
        },
        {
          '@type': 'WebSite',
          '@id': '#website',
          name: 'Melbarrel',
          url: '/',
          publisher: { '@id': '#organization' },
          inLanguage: this.langStore.current(),
        },
        {
          '@type': 'SoftwareApplication',
          name: 'Melbarrel',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description:
            // Sayı, ziyaretçinin üst menüden SAYABİLECEĞİ sayı olmalı: 30 sektör
            // sayfası var ('solutions/*' 31 tane ama biri web sitesi kurucusu).
            'Melbarrel combines customer records, scheduling, staff management, quoting, '
            + 'invoicing, online payments and a website builder into one platform, with 30 '
            + 'industry-specific modules.',
          // Fiyat iddiası, sayfada GÖRÜNEN fiyatla birebir aynı olmalı. Burada
          // bir dönem "ücretsiz plan" yazıyordu; öyle bir plan yok ve yapılandırılmış
          // veride olmayan bir teklifi ilan etmek Google'ın "yanıltıcı fiyat"
          // ihlalidir. Aralık /pricing sayfasındaki paketlerden gelir.
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'CAD',
            offerCount: 4,
            // Fiyat sayfası ZİYARETÇİNİN dilinde gösterilmeli. Sabit '/en/pricing'
            // yazılıydı: Arapça bir sonuçtan gelen kullanıcı İngilizce sayfaya
            // düşüyordu ve Google, o dilde zaten var olan sayfayı değil bunu
            // teklifin adresi sayıyordu.
            url: `/${this.langStore.current()}/pricing`,
          },
          publisher: { '@id': '#organization' },
        },
      ],
    });
  }

  constructor() {
    // Çıpa kaydırmasında yapışkan menünün altına gizlenmeyelim.
    //
    // Angular'ın ViewportScroller'ı çıpaya giderken CSS `scroll-margin-top`'u
    // YOK SAYAR (elemanı tam viewport tepesine oturtur) — bölüm başlığı 68px'lik
    // yapışkan menünün ARDINDA kalıyordu. Ofset burada veriliyor.
    //
    // Scroller kök singleton, yani ayar teknik olarak uygulama geneli. Panel ve
    // portal çıpa kullanmadığı için pratikte yalnız vitrini etkiler.
    //
    // Ofset SABİT DEĞİL, fonksiyon: menünün yüksekliği duyuru bandına (CMS'ten
    // gelir, açılıp kapanabilir) ve kırılma noktasına göre değişiyor. Sabit 96px
    // duyuru bandı açıkken 13px eksik kalıyor, bant kapatılınca fazla kaçıyordu.
    // ViewportScroller fonksiyon kabul ediyor, o yüzden her kaydırmada ölçüyoruz.
    if (this.isBrowser) this.scroller.setOffset(() => [0, this.stickyHeaderHeight()]);

    // <html lang> / <html dir> BURADA YAZILMAZ.
    //
    // İlk denemede seçilen dile göre yazılıyordu ve '/ar/pricing' henüz
    // çevrilmemişken şu çıktı: ekranda İngilizce metin, ama sayfa sağdan sola
    // hizalanmış — okunmaz. Doğrusu ÇİZİLEN İÇERİĞİN dili, ki onu ancak sayfa
    // yanıtı bilir. Bu yüzden ikisini de SeoService.apply() yazıyor
    // (bkz. core/seo.service.ts).

    // Master DİL BAŞINA tekil — dil değişirse çerçeve de değişir.
    effect(() => {
      const lang = this.langStore.current();

      this.cms.getMasterSections(lang).subscribe({
        next: (sections) => this.applyMaster(sections, lang),
        // Çerçeve çekilemezse sayfa içeriği yine görünsün — menüsüz bir sayfa,
        // hiç açılmayan bir sayfadan iyidir.
        error: () => this.master.set({ top: [], bottom: [] }),
      });
    });
  }

  /**
   * Çerçeveyi yerleştirir; o dilde Ana Şablon YOKSA varsayılan dilinkine düşer.
   *
   * SAYFA İÇERİĞİ için fallback YOK (yanlış dilde içerik = duplicate content +
   * yanıltıcı) — ama ÇERÇEVE içerik değil, gezinme. Bu ayrım olmadan şu oluyordu:
   * bir dil aktifleştirilip henüz sayfaları çevrilmemişse o dildeki her URL
   * 404 dönüyor, üstelik menüsü ve alt bilgisi de OLMAYAN çıplak bir 404
   * dönüyordu — ziyaretçi sitenin dışına düşüyor, geri dönecek tek bir bağlantı
   * bile bulamıyordu. (Türkçe açılıp test edilince görüldü.)
   */
  private applyMaster(sections: WebSection[], lang: string): void {
    const fallback = this.langStore.defaultCode();

    if (sections.length === 0 && lang !== fallback) {
      this.cms.getMasterSections(fallback).subscribe({
        next: (fallbackSections) => this.master.set(splitMaster(fallbackSections)),
        error: () => this.master.set({ top: [], bottom: [] }),
      });
      return;
    }

    this.master.set(splitMaster(sections));
  }

  /** Yapışkan menünün o anki yüksekliği. Menü henüz çizilmediyse yedek değer. */
  private stickyHeaderHeight(): number {
    const nav = document.querySelector('mkt-navbar');
    const height = nav?.getBoundingClientRect().height ?? 0;
    return (height || STICKY_HEADER_FALLBACK) + ANCHOR_BREATHING_ROOM;
  }
}
